import { COMMA, ENTER, SEMICOLON, SPACE, TAB } from '@angular/cdk/keycodes';
import { Component, ElementRef, Inject, OnDestroy, OnInit, Pipe, PipeTransform, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipGrid, MatChipInputEvent } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddUserDialogComponent, User, UserAction } from '@vendasta/users';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export interface SendEmailDialogData {
  allUsers: User[];
  partnerId: string;
  businessId: string;
  allowUserManagement: boolean;
}

export interface SendEmailDialogResp {
  userIds: string[];
}

@Component({
  templateUrl: './send-email-dialog.component.html',
  styleUrls: ['./send-email-dialog.component.scss'],
})
export class SendEmailDialogComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  allUsers: User[];
  separatorKeysCodes: number[] = [ENTER, COMMA, SPACE, SEMICOLON, TAB];
  selectedUsers: User[];
  emailControl: UntypedFormControl;
  filteredUsers$$: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
  filteredUsers$: Observable<User[]> = this.filteredUsers$$.asObservable();
  errorMessage$$: Subject<string> = new Subject();
  childDialogRef: MatDialogRef<AddUserDialogComponent>;

  partnerId: string;
  businessId: string;
  allowUserManagement: boolean;

  @ViewChild('emailInput') emailInput: ElementRef<HTMLInputElement>;
  @ViewChild('autoEmail') matAutocomplete: MatAutocomplete;
  @ViewChild('emailList') emailChipList: MatChipGrid;

  constructor(
    public dialogRef: MatDialogRef<SendEmailDialogComponent, SendEmailDialogResp>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: SendEmailDialogData,
  ) {
    this.allUsers = data?.allUsers || [];
    this.partnerId = data?.partnerId;
    this.businessId = data?.businessId;
    this.allowUserManagement = data?.allowUserManagement;
    this.selectedUsers = [];
  }

  ngOnInit(): void {
    this.emailControl = new UntypedFormControl(null);
    this.subscriptions.push(
      this.emailControl.valueChanges
        .pipe(
          startWith(null),
          map((value: string | null) => (value ? this._filter(value) : this.allUsers.slice())),
        )
        .subscribe((users) => {
          this.filteredUsers$$.next(users);
        }),
    );
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      const user = this.allUsers.find((u) => u.userId === value);
      if (user) {
        this._pushUser(user);
      }
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const user = this.allUsers.find((u) => u.userId === event.option.value);
    if (user) {
      this._pushUser(user);
    }
    this.emailInput.nativeElement.value = null;
    this.emailControl.setValue(null);
  }

  preventBlur(e: Event): void {
    e.stopPropagation();
    e.preventDefault();
  }

  remove(userId: string): void {
    const index = this.selectedUsers.indexOf(this.selectedUsers.find((u) => u.userId === userId));
    if (index >= 0) {
      this.selectedUsers.splice(index, 1);
      this._updateErrorState();
    }
  }

  private _pushUser(user: User): void {
    this.selectedUsers.push(user);
    this._updateErrorState();
  }

  // conventional error handling on a form control do not work with MatChipLists, this function handles the error state manually
  private _updateErrorState(): void {
    if (this.selectedUsers.length === 0) {
      this.errorMessage$$.next('At least one user is required');
      this.emailChipList.errorState = true;
      return;
    }

    this.errorMessage$$.next('');
    this.emailChipList.errorState = false;
    return;
  }

  close(save: boolean): void {
    const userIds = this.selectedUsers.map((u) => u.unifiedId);
    save ? this.dialogRef.close({ userIds: userIds }) : this.dialogRef.close();
  }

  presentAddUserDialog(): void {
    this.childDialogRef = this.dialog.open(AddUserDialogComponent, {
      width: '620px',
      data: {
        showCreateTab: true,
        showAddTab: false,
        partnerId: this.partnerId,
        businessId: this.businessId,
        existingUsers: this.allUsers,
      },
    });
    this.subscriptions.push(
      this.childDialogRef.afterClosed().subscribe((result) => {
        const users = this.filteredUsers$$.getValue();

        if (result?.users.length && result?.users.length > 0 && result?.action === UserAction.ADD) {
          users.push(...result.users);
          this.filteredUsers$$.next(users);
          this.allUsers.push(...result.users);
          this.selectedUsers.push(...result.users);
        } else if (result?.users.length && result?.users.length > 0 && result?.action === UserAction.CREATE) {
          users.push(result.users[0]);
          this.filteredUsers$$.next(users);
          this.allUsers.push(result.users[0]);
          this.selectedUsers.push(result.users[0]);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private _filter(value: string): User[] {
    const filterValue = value.toLowerCase();
    return this.allUsers.filter((u) => u.email.toLowerCase().indexOf(filterValue) === 0);
  }

  // formatUser returns a User formatted as: jsmith47@example.ca (John Smith)
  static formatUser(user: User): string {
    let formattedUser = '';
    if (user?.email) {
      formattedUser = user.email;
    }

    const names: string[] = [];
    if (user.firstName) {
      names.push(user.firstName);
    }
    if (user.lastName) {
      names.push(user.lastName);
    }
    if (names.length > 0) {
      formattedUser = `${formattedUser} (${names.join(' ')})`;
    }
    return formattedUser;
  }
}

@Pipe({
  name: 'formatUser',
  standalone: true,
})
export class FormatUserPipe implements PipeTransform {
  transform(user: User): string {
    return SendEmailDialogComponent.formatUser(user);
  }
}
